import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup } from "react-bootstrap";
import { Popconfirm } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { closeCustomModal, openCustomModal } from "components/custom/modals";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";

import {
    fetchServiceTypes,
    deleteServiceType,
    clearServiceTypes
} from "api/menu/master-files/service-types/actions";

const ServiceTypeEditModal = React.lazy(() =>
    import("components/menu/master-files/service-types/create-edit-modal"));

class ServiceTypesList extends React.Component {
    constructor(props) {
        super(props);

        this.modalOpen = this.modalOpen.bind(this);
        this.delete = this.delete.bind(this);
    }

    componentDidMount() {
        this.props.fetchServiceTypes(true);
    }

    componentWillUnmount() {
        this.props.clearServiceTypes();
    }

    modalOpen = (serviceTypeId = false) => {
        const modalTitle = serviceTypeId !== false ? "Update" : "Create";

        const modalObj = {
            id: `service-type-${serviceTypeId}`,
            header: `${modalTitle} Service Type`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ServiceTypeEditModal
                        serviceTypeId={serviceTypeId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    delete = (id, code) => {
        this.props.deleteServiceType(id, code, () => this.props.fetchServiceTypes(true));
    };

    // eslint-disable-next-line class-methods-use-this
    render () {
        const {
            serviceTypes,
            loading
        } = this.props;
        const classNameFlexCenter = "d-flex justify-content-center align-items-center";

        const columns = [
            {
                Header: "Service Code",
                accessor: "code",
                id: "code",
            },
            {
                Header: "Description",
                accessor: "name",
                id: "description1",
            },
            {
                Header: "Active?",
                id: "isInactive",
                accessor: original => (original.isInactive ? "NO" : "YES"),
                Cell: ({ original }) => (original.isInactive ? "NO" : "YES"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Active",
                            NO: "Inactive"
                        }}
                    />
                )
            },
            {
                Header: "Actions",
                id: "actions",
                className: classNameFlexCenter,
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Button
                            size="sm"
                            onClick={() => this.modalOpen(original.id)}
                        >
                            <FontAwesomeIcon icon="pen" title="Update" />
                        </Button>
                        <Popconfirm
                            title={`Delete "${original.code}"?`}
                            onConfirm={() => this.delete(original.id, original.code)}
                            placement="bottomRight"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button variant="danger" size="sm">
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </Popconfirm>
                    </ButtonGroup>
                ),
                filterable: false,
                width: 100
            },
        ];

        return (
            <div className="dashboard">
                <h3 className="mb-4">
                    Service Types
                </h3>

                <ReactTable
                    data={serviceTypes}
                    headerClassName="table-header"
                    columns={columns}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    defaultFilterMethod={filterCaseInsensitive}
                    defaultSorted={[
                        {
                            id: "code",
                            desc: false
                        }
                    ]}
                    defaultFiltered={[
                        {
                            id: "isInactive",
                            value: ""
                        }
                    ]}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    style={{
                        height: "calc(100vh - 200px)",
                    }}
                />

                <div className="mt-4 mb-4 text-right">
                    <Button className="ml-2" variant="primary" onClick={() => this.modalOpen()}>
                        Add New Service Type
                    </Button>
                </div>
            </div>
        );
    }
}

ServiceTypesList.propTypes = {
    fetchServiceTypes: PropTypes.func.isRequired,
    deleteServiceType: PropTypes.func.isRequired,
    clearServiceTypes: PropTypes.func.isRequired,

    serviceTypes: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
    serviceTypes: state.serviceTypes.serviceTypes,
    loading: state.serviceTypes.loading,
});

const mapStateToDispatch = dispatch => ({
    fetchServiceTypes: bindActionCreators(fetchServiceTypes, dispatch),
    deleteServiceType: bindActionCreators(deleteServiceType, dispatch),
    clearServiceTypes: bindActionCreators(clearServiceTypes, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ServiceTypesList);
